/**
 * Maps country names (as returned by GA4/API or the boundary dataset) to ISO 3166-1 alpha-3 codes.
 * Used to join dashboard region rows to country boundaries.
 */
export const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  Afghanistan: "AFG",
  Albania: "ALB",
  Algeria: "DZA",
  Argentina: "ARG",
  Australia: "AUS",
  Austria: "AUT",
  Bangladesh: "BGD",
  Belgium: "BEL",
  "Bosnia and Herz.": "BIH",
  "Bosnia and Herzegovina": "BIH",
  Brazil: "BRA",
  Bulgaria: "BGR",
  Canada: "CAN",
  "Central African Rep.": "CAF",
  "Central African Republic": "CAF",
  Chile: "CHL",
  China: "CHN",
  Colombia: "COL",
  Congo: "COG",
  "Republic of the Congo": "COG",
  "Dem. Rep. Congo": "COD",
  "Democratic Republic of the Congo": "COD",
  "DR Congo": "COD",
  "Congo (Kinshasa)": "COD",
  "Congo (Brazzaville)": "COG",
  "Czech Republic": "CZE",
  Czechia: "CZE",
  Denmark: "DNK",
  "Dominican Rep.": "DOM",
  "Dominican Republic": "DOM",
  Egypt: "EGY",
  "Eq. Guinea": "GNQ",
  "Equatorial Guinea": "GNQ",
  Eritrea: "ERI",
  Eswatini: "SWZ",
  eSwatini: "SWZ",
  Estonia: "EST",
  Ethiopia: "ETH",
  Finland: "FIN",
  France: "FRA",
  Germany: "DEU",
  Greece: "GRC",
  "Hong Kong": "HKG",
  Hungary: "HUN",
  India: "IND",
  Indonesia: "IDN",
  Iran: "IRN",
  Iraq: "IRQ",
  Ireland: "IRL",
  Israel: "ISR",
  Italy: "ITA",
  Japan: "JPN",
  Jordan: "JOR",
  Kenya: "KEN",
  Korea: "KOR",
  "South Korea": "KOR",
  "Republic of Korea": "KOR",
  "North Korea": "PRK",
  Kuwait: "KWT",
  Lebanon: "LBN",
  Macedonia: "MKD",
  "North Macedonia": "MKD",
  Malaysia: "MYS",
  Mexico: "MEX",
  Morocco: "MAR",
  Netherlands: "NLD",
  "New Zealand": "NZL",
  Nigeria: "NGA",
  Norway: "NOR",
  Palestine: "PSE",
  Pakistan: "PAK",
  Peru: "PER",
  Philippines: "PHL",
  Poland: "POL",
  Portugal: "PRT",
  Romania: "ROU",
  Russia: "RUS",
  "Russian Federation": "RUS",
  "Saudi Arabia": "SAU",
  Singapore: "SGP",
  "South Sudan": "SSD",
  "S. Sudan": "SSD",
  "South Africa": "ZAF",
  Spain: "ESP",
  "Sri Lanka": "LKA",
  Sweden: "SWE",
  Switzerland: "CHE",
  Tanzania: "TZA",
  Taiwan: "TWN",
  Thailand: "THA",
  Turkey: "TUR",
  Ukraine: "UKR",
  "United Arab Emirates": "ARE",
  UAE: "ARE",
  "United Kingdom": "GBR",
  UK: "GBR",
  "United States": "USA",
  "United States of America": "USA",
  USA: "USA",
  US: "USA",
  Venezuela: "VEN",
  Vietnam: "VNM",
  "Viet Nam": "VNM",
  "W. Sahara": "ESH",
  "Western Sahara": "ESH",
  Yemen: "YEM",
  Zimbabwe: "ZWE",
};

function normalize(name: string): string {
  return name.trim();
}

const LOWER_NAME_TO_ISO = new Map<string, string>();
for (const [k, v] of Object.entries(COUNTRY_NAME_TO_ISO)) {
  LOWER_NAME_TO_ISO.set(k.toLowerCase(), v);
}

export function countryNameToIso(name: string): string | undefined {
  const n = normalize(name);
  return COUNTRY_NAME_TO_ISO[n] ?? LOWER_NAME_TO_ISO.get(n.toLowerCase());
}

/**
 * [lng, lat] anchors for pins and flyTo where polygon centroids sit away from map labels.
 * Falls back to {@link ISO_TO_CENTER} when omitted.
 */
const ISO_TO_MAP_PIN_ANCHOR: Partial<Record<string, [number, number]>> = {
  PAK: [73.0479, 33.6844],
  BRA: [-47.8825, -15.7939],
  IND: [77.209, 28.6139],
  CAN: [-75.6972, 45.4215],
  AUS: [149.1309, -35.2804],
  RUS: [37.6173, 55.7558],
  CHN: [116.4074, 39.9042],
  IDN: [106.8451, -6.2088],
  ARG: [-58.3816, -34.6037],
  MEX: [-99.1332, 19.4326],
  SAU: [46.6753, 24.7136],
  NGA: [7.4951, 9.0579],
  ZAF: [28.0473, -26.2041],
  EGY: [31.2357, 30.0444],
  TUR: [32.8597, 39.9334],
  GBR: [-0.1276, 51.5074],
  FRA: [2.3522, 48.8566],
  ITA: [12.4964, 41.9028],
  ESP: [-3.7038, 40.4168],
  DEU: [13.405, 52.52],
  JPN: [139.6917, 35.6895],
  KOR: [126.978, 37.5665],
  THA: [100.5018, 13.7563],
  VNM: [105.8342, 21.0278],
  MYS: [101.6869, 3.139],
  PHL: [120.9842, 14.5995],
  POL: [21.0122, 52.2297],
  UKR: [30.5234, 50.4501],
  IRN: [51.389, 35.6892],
  IRQ: [44.3661, 33.3152],
  ARE: [54.3773, 24.4539],
  SGP: [103.8198, 1.3521],
};

/** Approximate [longitude, latitude] polygon centroids — fallback when no pin anchor */
export const ISO_TO_CENTER: Record<string, [number, number]> = {
  AFG: [67.71, 33.94],
  ALB: [20.17, 41.15],
  DZA: [2.55, 28.03],
  ARG: [-63.62, -38.42],
  AUS: [133.78, -25.27],
  AUT: [14.55, 47.52],
  BGD: [90.36, 23.68],
  BEL: [4.47, 50.5],
  BGR: [25.49, 42.73],
  BIH: [17.68, 43.92],
  BRA: [-55.78, -10.0],
  CAF: [20.94, 6.61],
  CAN: [-106.35, 56.13],
  CHE: [8.23, 46.82],
  CHL: [-71.54, -35.68],
  CHN: [105.0, 35.0],
  COG: [15.83, -0.23],
  COD: [21.76, -4.04],
  COL: [-74.0, 4.0],
  CZE: [15.47, 49.82],
  DEU: [10.45, 51.17],
  DNK: [9.5, 56.26],
  DOM: [-70.16, 18.74],
  EGY: [30.8, 26.82],
  ERI: [39.78, 15.18],
  ESP: [-3.75, 40.46],
  EST: [25.59, 58.6],
  ETH: [40.49, 9.15],
  FIN: [25.74, 61.92],
  FRA: [2.21, 46.23],
  GBR: [-2.59, 54.05],
  GNQ: [10.27, 1.65],
  GRC: [21.82, 39.08],
  HKG: [114.17, 22.32],
  HUN: [19.5, 47.16],
  IDN: [113.92, -0.79],
  IND: [78.96, 20.59],
  IRL: [8.24, 53.41],
  IRN: [53.69, 32.43],
  IRQ: [43.68, 33.22],
  ISR: [34.75, 31.05],
  ITA: [12.57, 41.87],
  JOR: [36.24, 30.58],
  JPN: [138.25, 36.2],
  KEN: [37.91, -0.02],
  KOR: [127.77, 35.91],
  KWT: [47.48, 29.31],
  LBN: [35.86, 33.85],
  LKA: [80.77, 7.87],
  MAR: [-7.09, 31.79],
  MKD: [21.75, 41.61],
  MEX: [-102.55, 23.63],
  MYS: [101.98, 4.21],
  NGA: [8.68, 9.08],
  NLD: [5.29, 52.13],
  NOR: [8.47, 60.47],
  NZL: [174.89, -40.9],
  PAK: [69.35, 30.38],
  PER: [-75.02, -9.19],
  PHL: [121.77, 12.88],
  POL: [19.39, 51.92],
  PRK: [127.51, 40.34],
  PRT: [-8.22, 39.4],
  PSE: [35.23, 31.95],
  ROU: [24.97, 45.94],
  RUS: [100.0, 60.0],
  SAU: [45.08, 23.88],
  SGP: [103.82, 1.35],
  SSD: [30.22, 6.88],
  SWE: [15.63, 62.2],
  SWZ: [31.47, -26.52],
  THA: [100.99, 15.87],
  TUN: [9.54, 33.89],
  TUR: [35.24, 38.96],
  TWN: [120.96, 23.7],
  TZA: [34.89, -6.37],
  UKR: [31.17, 48.38],
  USA: [-98.58, 39.83],
  VEN: [66.59, 6.42],
  VNM: [108.28, 14.06],
  YEM: [48.52, 15.55],
  ZAF: [25.74, -29.0],
  ZWE: [29.15, -19.02],
  ARE: [54.37, 24.45],
};

export function getCountryCenter(iso: string): [number, number] | undefined {
  const pinAnchor = ISO_TO_MAP_PIN_ANCHOR[iso];
  if (pinAnchor) {
    return pinAnchor;
  }
  return ISO_TO_CENTER[iso];
}
